import { useMemo, useState } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, fmtPct, WEEKDAYS_SK_SHORT } from "@/lib/app";
import PinButton from "@/components/PinButton";
import PeriodOverrideBar from "@/components/PeriodOverrideBar";

const fetcher = (url) => api.get(url).then((r) => r.data);

function colorForEff(pct) {
  if (pct >= 110) return { bg: "#3E7B52", fg: "white", label: "vysoká" };
  if (pct >= 90) return { bg: "#7FAB8C", fg: "white", label: "dobrá" };
  if (pct >= 70) return { bg: "#E8C99A", fg: "#5E4A20", label: "stredná" };
  if (pct >= 50) return { bg: "#E09B6E", fg: "white", label: "nízka" };
  return { bg: "#BA3F2B", fg: "white", label: "kritická" };
}

export default function HeatmapCalendar({ storeId, period }) {
  const [localPeriod, setLocalPeriod] = useState(period);
  const qs = `period_from=${localPeriod?.from || ""}&period_to=${localPeriod?.to || ""}`;
  const { data } = useSWR(`/stores/${storeId}/analytics/daily?${qs}`, fetcher);
  const [metric, setMetric] = useState("efficiency_pct"); // or "revenue"

  const months = useMemo(() => {
    if (!data?.days) return [];
    const map = new Map();
    for (const d of data.days) {
      const dt = new Date(d.date);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      if (!map.has(key)) map.set(key, { key, year: dt.getFullYear(), month: dt.getMonth(), days: [] });
      map.get(key).days.push({ ...d, dt });
    }
    return Array.from(map.values()).sort((a, b) => (a.key > b.key ? -1 : 1));
  }, [data]);

  if (!data) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie heatmapy" testIdPrefix="heatmap-period" />
      <div className="text-[var(--text-secondary)]">Načítavam...</div>
    </div>
  );
  if (data.empty) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie heatmapy" testIdPrefix="heatmap-period" />
      <div className="card-flat p-8 text-center text-[var(--text-secondary)]">V tomto období nie sú dáta.</div>
    </div>
  );

  const monthNames = ["Január", "Február", "Marec", "Apríl", "Máj", "Jún", "Júl", "August", "September", "Október", "November", "December"];

  // for revenue metric, build percentile ranges
  const revenues = data.days.map((d) => d.revenue).sort((a, b) => a - b);
  const p = (q) => revenues[Math.min(revenues.length - 1, Math.floor(revenues.length * q))] || 0;
  const revColor = (rev) => {
    if (rev >= p(0.8)) return colorForEff(120);
    if (rev >= p(0.6)) return colorForEff(95);
    if (rev >= p(0.4)) return colorForEff(80);
    if (rev >= p(0.2)) return colorForEff(60);
    return colorForEff(40);
  };
  // same for customers (purchases count per day)
  const customers = data.days.map((d) => d.customers || 0).sort((a, b) => a - b);
  const pc = (q) => customers[Math.min(customers.length - 1, Math.floor(customers.length * q))] || 0;
  const custColor = (c) => {
    if (c <= 0) return { bg: "#F3F5F4", fg: "#8B948E", label: "bez dát" };
    if (c >= pc(0.8)) return colorForEff(120);
    if (c >= pc(0.6)) return colorForEff(95);
    if (c >= pc(0.4)) return colorForEff(80);
    if (c >= pc(0.2)) return colorForEff(60);
    return colorForEff(40);
  };

  const metricLabel = metric === "efficiency_pct"
    ? "efektívnosť (€/os.hod vs požadovaný výkon)"
    : metric === "revenue"
    ? "celkovú dennú tržbu"
    : "počet nákupov (zákazníkov) za deň";

  return (
    <div className="space-y-5">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie heatmapy" testIdPrefix="heatmap-period" />
      <div className="card-flat p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-start gap-2">
            <div>
              <h3 className="font-display text-base font-semibold">Výkonnosť podľa dní</h3>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Sila farby zobrazuje {metricLabel}. Mesiace usporiadané od najnovšieho po najstarší.
              </p>
            </div>
            <PinButton
              storeId={storeId}
              kind="chart"
              label={`Výkonnosť podľa dní — ${metric === "efficiency_pct" ? "efektívnosť" : metric === "revenue" ? "tržba" : "nákupy"} (${period.from} → ${period.to})`}
              data={{ id: `heatmap_${metric}_${period.from}_${period.to}`, metric, source: "heatmap_calendar" }}
              period={period}
              testId="pin-chart-heatmap"
            />
          </div>
          <div className="flex gap-2">
            <button
              className={`btn-ghost text-xs ${metric === "efficiency_pct" ? "bg-[var(--bg-surface-hover)]" : ""}`}
              onClick={() => setMetric("efficiency_pct")}
              data-testid="heatmap-metric-eff"
            >
              Efektívnosť
            </button>
            <button
              className={`btn-ghost text-xs ${metric === "revenue" ? "bg-[var(--bg-surface-hover)]" : ""}`}
              onClick={() => setMetric("revenue")}
              data-testid="heatmap-metric-rev"
            >
              Tržba
            </button>
            <button
              className={`btn-ghost text-xs ${metric === "customers" ? "bg-[var(--bg-surface-hover)]" : ""}`}
              onClick={() => setMetric("customers")}
              data-testid="heatmap-metric-cust"
            >
              Počet nákupov
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="heatmap-grid">
          {months.map((m) => {
            const daysByDate = new Map(m.days.map((d) => [d.dt.getDate(), d]));
            const firstDow = new Date(m.year, m.month, 1).getDay(); // 0=Ne
            const offset = (firstDow + 6) % 7; // make Mon=0
            const lastDate = new Date(m.year, m.month + 1, 0).getDate();
            const cells = [];
            for (let i = 0; i < offset; i++) cells.push(null);
            for (let d = 1; d <= lastDate; d++) cells.push(d);

            return (
              <div key={m.key} className="border border-[var(--border-default)] rounded-lg p-3">
                <h4 className="font-display text-sm font-semibold mb-2">
                  {monthNames[m.month]} {m.year}
                </h4>
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {WEEKDAYS_SK_SHORT.map((w) => (
                    <div key={w} className="text-[10px] text-center text-[var(--text-muted)] font-medium">
                      {w}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((d, idx) => {
                    if (d === null) return <div key={idx} />;
                    const day = daysByDate.get(d);
                    if (!day) {
                      return (
                        <div
                          key={idx}
                          className="aspect-square rounded text-[10px] flex items-center justify-center text-[var(--text-muted)] bg-[var(--bg-page)]"
                          title="Zatvorené"
                        >
                          {d}
                        </div>
                      );
                    }
                    const c = metric === "efficiency_pct"
                      ? colorForEff(day.efficiency_pct)
                      : metric === "revenue"
                      ? revColor(day.revenue)
                      : custColor(day.customers || 0);
                    const cellValue = metric === "efficiency_pct"
                      ? Math.round(day.efficiency_pct)
                      : metric === "revenue"
                      ? Math.round(day.revenue)
                      : Math.round(day.customers || 0);
                    return (
                      <div
                        key={idx}
                        className="aspect-square rounded text-[10px] flex flex-col items-center justify-center cursor-default relative group/cell"
                        style={{ background: c.bg, color: c.fg }}
                        title={`${day.date} ${day.weekday_name} · Tržba ${fmtEur(day.revenue)} · ${fmtNum(day.customers || 0, 0)} nákupov · košík ${fmtEur(day.basket || 0)} · Osobohodiny ${fmtNum(day.employees, 1)} h · €/os.hod ${fmtEur(day.per_person_hour)} · Efektívnosť ${fmtPct(day.efficiency_pct)}`}
                        data-testid={`heatmap-cell-${day.date}`}
                      >
                        <span className="font-semibold leading-none">{d}</span>
                        <span className="text-[9px] tabular leading-none mt-0.5">{cellValue}</span>
                        <div className="absolute -top-1 -right-1 opacity-0 group-hover/cell:opacity-100 transition-opacity" style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }}>
                          <PinButton
                            storeId={storeId}
                            kind="day"
                            label={`Deň ${day.date} (${day.weekday_name}): tržba ${fmtEur(day.revenue)}, ${fmtNum(day.employees, 1)} os.hod, €/os.hod ${fmtEur(day.per_person_hour)}, efektívnosť ${fmtPct(day.efficiency_pct)}`}
                            data={{ date: day.date, source: "heatmap_calendar", ...day }}
                            period={period}
                            testId={`pin-heatmap-day-${day.date}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-5 text-xs">
          <span className="text-[var(--text-secondary)]">Legenda:</span>
          {[
            { lbl: "<50%", c: colorForEff(40) },
            { lbl: "50-70%", c: colorForEff(60) },
            { lbl: "70-90%", c: colorForEff(80) },
            { lbl: "90-110%", c: colorForEff(95) },
            { lbl: "≥110%", c: colorForEff(120) },
          ].map((l) => (
            <span key={l.lbl} className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded" style={{ background: l.c.bg }} />
              {l.lbl}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
